"use client";

import { Thread } from '@/components/assistant-ui/thread';
import { ThreadList } from '@/components/assistant-ui/thread-list';
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useApp } from '@/contexts/app-context';
import { ProjectChatGuard } from '@/pages/ai-chat/remote-threadlist-adapter';
import { useAssistantApi } from "@assistant-ui/react";
import { ArrowLeft, History, Maximize2, MessageCircle, PlusIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";



export function ChatSheet({ trigger }: { trigger?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'chat' | 'list'>('chat');

  // Reset view when opening
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      // Ideally we check if a thread is selected, but 'chat' default is fine
      // If no thread selected, Thread component usually handles it or shows empty
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="relative">
            <MessageCircle className="h-5 w-5" />
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" hideClose className="w-full sm:max-w-[800px] sm:w-[800px] flex flex-col p-0 gap-0">
        <ProjectChatGuard>
          <ChatSheetContent view={view} setView={setView} onClose={() => setOpen(false)} />
        </ProjectChatGuard>
      </SheetContent>
    </Sheet>
  );
}

function ChatSheetContent({ view, setView, onClose }: { view: 'chat' | 'list', setView: (v: 'chat' | 'list') => void, onClose: () => void }) {
  const api = useAssistantApi();
  const { projectId } = useApp();
  const navigate = useNavigate();

  const handleNewChat = () => {
    api.threads().switchToNewThread();
    setView('chat');
  };

  return (
    <>
      <SheetHeader className="px-4 py-3 border-b flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          {view === 'list' && (
            <Button variant="ghost" size="icon" onClick={() => setView('chat')} className="-ml-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <SheetTitle>
            {view === 'list' ? 'Threads' : 'Chat'}
          </SheetTitle>
        </div>
        <div className="flex items-center gap-2">
          {view === 'chat' && (
            <Button variant="ghost" size="sm" onClick={() => setView('list')} className="text-muted-foreground">
              <History className="h-4 w-4 mr-2" />
              History
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => {
            onClose();
            navigate(`/projects/${projectId}/ai-chat`);
          }} title="Open Full Page">
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleNewChat} title="New Chat">
            <PlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-hidden relative flex flex-col">
        {view === 'list' ? (
          <div className="p-4 overflow-y-auto scrollbar-hide flex-1">
            <ThreadList onSelect={() => setView('chat')} />
          </div>
        ) : (
          <div className="flex-1 h-full min-h-0">
            <Thread viewportClassName="px-6" />
          </div>
        )}
      </div>
    </>
  )
}
